import React from "react";
import { Repeat, Compass, Layers, Wind, Activity, Sprout } from "lucide-react";
import ApplicationTemplate from "@/components/applications/ApplicationTemplate";

const HabitChange = () => (
  <ApplicationTemplate
    pageKey="habit-change"
    breadcrumbLabel="Habit & Behavior Change"
    overline="Application · Habit & Behavior Change"
    headline="Habits are outputs of a state,"
    headlineAccent="not proofs of character."
    lead="When a habit resists every attempt to change it, the physiological state that produces it usually hasn’t changed. Adaptive Physiology intervenes at the state — where behavior actually originates."
    loopHighlight="behavior"
    challenge={{
      title: "The habit is downstream. The state is upstream.",
      paragraphs: [
        "Recurring behaviors — scrolling, snacking, avoidance, procrastination — are efficient physiological solutions to the state a person is currently in. Boredom, low arousal, sympathetic activation, disrupted sleep, or dopamine flattening each produce their own predictable outputs.",
        "This is why habits change temporarily, then snap back. The behavior was never the problem. The state kept generating the behavior.",
      ],
    }}
    traditionalGap={{
      title: "Habit systems assume conscious control of an unconscious output.",
      paragraphs: [
        "Streaks, apps, and habit stacks are helpful scaffolds — but they operate as if the executive system is always available. Under a dysregulated state, it isn’t.",
        "Adaptive Physiology doesn’t discard structure. It adds the physiological layer underneath — retraining the state so the structure has something regulated to run on.",
      ],
    }}
    perspective={{
      title: "Change the state. The habit follows.",
      paragraphs: [
        "Exercise physiology, environmental design, sleep architecture, and rhythmic recovery are used to shift the state that most reliably produces the unwanted behavior.",
        "When the underlying physiology changes, the behavior loses its usefulness — it stops solving a problem the body no longer has.",
      ],
    }}
    frameworkApplication={[
      {
        title: "Baseline the state–behavior pattern",
        body: "Which physiological states most reliably precede the behavior? Sleep, HRV, energy, and context are captured alongside the habit itself.",
      },
      {
        title: "Prescribe the state-shifting stimulus",
        body: "Precisely dosed exercise, environmental structure, and recovery inputs matched to the state that most reliably produces the pattern.",
      },
      {
        title: "Reinforce with structure",
        body: "Daily and weekly cadence, environmental cues, and accountability keep the new signal landing until the state begins to shift.",
      },
      {
        title: "Retrain the default output",
        body: "As the underlying state changes, the habit loses its grip. New behavior emerges as the natural output of a new default.",
      },
    ]}
    capacities={[
      { icon: Repeat, label: "Behavioral flexibility", body: "More options in the space between trigger and response." },
      { icon: Compass, label: "Self-efficacy", body: "Confidence that has been earned physiologically, not asserted." },
      { icon: Layers, label: "Consistency capacity", body: "The state required to run a structure without collapsing it." },
      { icon: Sprout, label: "New identity substrate", body: "A physiology congruent with the person you’re becoming." },
      { icon: Wind, label: "Craving latency", body: "Longer gaps between impulse and action." },
      { icon: Activity, label: "Effortful ease", body: "Behavior that no longer requires willpower to run." },
    ]}
  />
);
export default HabitChange;
